// A lesson's knowledge check (stored inline on the lesson document): title,
// duration and multiple-choice questions with four options. Adding, editing
// or deleting a question saves straight away, as the old admin did; the title
// and duration save with "Save knowledge check".

import { useState, type FormEvent } from "react";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { emptyKnowledgeCheck, type KnowledgeCheck } from "../../types/course";
import { errorText } from "../../services/courses/auth";
import { Modal } from "../Modal";
import {
  OPTION_LETTERS as LETTERS,
  buildQuestion,
  emptyQuestionDraft,
  questionToDraft,
  type QuestionDraft,
} from "./knowledgeCheck";

export function KnowledgeCheckManager({
  knowledgeCheck,
  onUpdate,
  disabled,
}: {
  knowledgeCheck?: KnowledgeCheck;
  // undefined removes the check from the lesson.
  onUpdate: (knowledgeCheck?: KnowledgeCheck) => Promise<void>;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState<KnowledgeCheck | null>(knowledgeCheck ?? null);
  // Re-seed the draft only when the stored check actually changes, so a
  // parent reload for an unrelated reason does not clobber an unsaved title.
  const propKey = JSON.stringify(knowledgeCheck ?? null);
  const [seenKey, setSeenKey] = useState(propKey);
  if (propKey !== seenKey) {
    setSeenKey(propKey);
    setDraft(knowledgeCheck ?? null);
  }

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // null: modal closed; -1: new question; n >= 0: editing question n.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [questionDraft, setQuestionDraft] = useState<QuestionDraft>(emptyQuestionDraft());
  const [questionError, setQuestionError] = useState<string | null>(null);

  // Writes the check and returns the error text, or null on success. Callers
  // decide where the error shows (the box footer or the question modal).
  async function persist(next: KnowledgeCheck | undefined): Promise<string | null> {
    setBusy(true);
    try {
      await onUpdate(next);
      return null;
    } catch (e) {
      return errorText(e);
    } finally {
      setBusy(false);
    }
  }

  async function removeCheck() {
    if (!confirm("Remove the knowledge check from this lesson?")) return;
    setError(null);
    if (knowledgeCheck) {
      const err = await persist(undefined);
      if (err) return setError(err);
    }
    setDraft(null);
  }

  async function saveCheck() {
    if (!draft) return;
    if (draft.questions.length === 0) {
      setError("Add at least one question before saving the check.");
      return;
    }
    setError(await persist(draft));
  }

  function openQuestion(index: number) {
    if (!draft) return;
    setQuestionError(null);
    setQuestionDraft(index >= 0 ? questionToDraft(draft.questions[index]) : emptyQuestionDraft());
    setEditingIndex(index);
  }

  function closeQuestion() {
    setEditingIndex(null);
    setQuestionError(null);
  }

  async function submitQuestion(e: FormEvent) {
    e.preventDefault();
    if (!draft || editingIndex === null) return;
    const built = buildQuestion(questionDraft, editingIndex >= 0 ? draft.questions[editingIndex].id : undefined);
    if ("error" in built) return setQuestionError(built.error);
    const { question } = built;
    const questions = [...draft.questions];
    if (editingIndex >= 0) questions[editingIndex] = question;
    else questions.push(question);
    const next = { ...draft, questions };
    setQuestionError(null);
    const err = await persist(next);
    if (err) return setQuestionError(err);
    // Only a saved question enters the draft, so a failed save cannot be retried into duplicates.
    setDraft(next);
    closeQuestion();
  }

  async function deleteQuestion(index: number) {
    if (!draft || !confirm(`Delete question ${index + 1}?`)) return;
    const next = { ...draft, questions: draft.questions.filter((_, i) => i !== index) };
    setError(null);
    const err = await persist(next);
    if (err) return setError(err);
    setDraft(next);
  }

  if (!draft) {
    return (
      <div className="co-empty-inline">
        No knowledge check for this lesson.
        <button type="button" className="btn btn-sm" onClick={() => setDraft(emptyKnowledgeCheck())} disabled={disabled}>
          <IconPlus size={14} /> Add knowledge check
        </button>
        {error && <span className="error-text">{error}</span>}
      </div>
    );
  }

  return (
    <div className="co-kc-box">
      <div className="co-kc-settings">
        <div>
          <label>Title</label>
          <input
            type="text"
            value={draft.title}
            placeholder="Knowledge Check"
            disabled={disabled}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
        </div>
        <div>
          <label>Duration (min)</label>
          <input
            type="number"
            min={1}
            value={draft.duration}
            disabled={disabled}
            onChange={(e) => setDraft({ ...draft, duration: parseInt(e.target.value, 10) || 5 })}
          />
        </div>
      </div>

      <div className="co-sub-head">
        Questions <span className="count">{draft.questions.length}</span>
        <div className="spacer" />
        <button type="button" className="btn btn-sm" onClick={() => openQuestion(-1)} disabled={disabled || busy}>
          <IconPlus size={14} /> Add question
        </button>
      </div>

      {draft.questions.length === 0 ? (
        <div className="co-empty-inline">No questions yet. Add at least one before saving the check.</div>
      ) : (
        draft.questions.map((q, i) => (
          <div key={q.id} className="co-question">
            <div className="co-q-head">
              <span className="n">Q{i + 1}</span>
              <span className="txt">{q.question}</span>
              <div className="co-actions">
                <button type="button" className="co-btn-icon" onClick={() => openQuestion(i)} disabled={disabled || busy}
                  title="Edit question" aria-label={`Edit question ${i + 1}`}>
                  <IconPencil size={14} />
                </button>
                <button type="button" className="co-btn-icon danger" onClick={() => deleteQuestion(i)} disabled={disabled || busy}
                  title="Delete question" aria-label={`Delete question ${i + 1}`}>
                  <IconTrash size={14} />
                </button>
              </div>
            </div>
            <div className="co-q-opts">
              {q.options.map((o, oi) => (
                <div key={oi} className={`co-opt${oi === q.correctAnswerIndex ? " correct" : ""}`}>
                  <span className="l">{LETTERS[oi]}</span>
                  <span>
                    {o}
                    {oi === q.correctAnswerIndex ? " ✓" : ""}
                  </span>
                </div>
              ))}
            </div>
            {q.explanation && <div className="co-q-expl">{q.explanation}</div>}
          </div>
        ))
      )}

      <div className="co-kc-foot">
        {error && <span className="error-text">{error}</span>}
        <div className="spacer" />
        <button type="button" className="btn btn-sm btn-ghost btn-danger" onClick={removeCheck} disabled={disabled || busy}>
          Remove
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={saveCheck}
          disabled={disabled || busy || draft.questions.length === 0}>
          {busy ? "Saving…" : "Save knowledge check"}
        </button>
      </div>

      <Modal
        open={editingIndex !== null}
        onClose={closeQuestion}
        title={editingIndex !== null && editingIndex >= 0 ? "Edit question" : "Add question"}
        footer={
          <>
            <button type="submit" form="co-question-form" className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : editingIndex !== null && editingIndex >= 0 ? "Update question" : "Add question"}
            </button>
            <button type="button" className="btn" onClick={closeQuestion} disabled={busy}>
              Cancel
            </button>
            {questionError && <span className="error-text">{questionError}</span>}
          </>
        }
      >
        <form id="co-question-form" onSubmit={submitQuestion}>
          <label htmlFor="co-q-text">Question</label>
          <textarea
            id="co-q-text"
            rows={2}
            value={questionDraft.question}
            placeholder="What is the main topic of this lesson?"
            onChange={(e) => setQuestionDraft({ ...questionDraft, question: e.target.value })}
          />
          <label>Options · pick the correct answer</label>
          {LETTERS.map((letter, i) => (
            <div key={letter} className="co-opt-in">
              <span className="l">{letter}</span>
              <input
                type="text"
                value={questionDraft.options[i]}
                placeholder={`Option ${letter}`}
                aria-label={`Option ${letter}`}
                onChange={(e) => {
                  const options = [...questionDraft.options];
                  options[i] = e.target.value;
                  setQuestionDraft({ ...questionDraft, options });
                }}
              />
              <label>
                <input
                  type="radio"
                  name="co-correct"
                  value={i}
                  checked={questionDraft.correctAnswerIndex === i}
                  onChange={() => setQuestionDraft({ ...questionDraft, correctAnswerIndex: i })}
                />
                correct
              </label>
            </div>
          ))}
          <label htmlFor="co-q-expl">
            Explanation <span className="muted">(optional, shown after answering)</span>
          </label>
          <textarea
            id="co-q-expl"
            rows={2}
            value={questionDraft.explanation}
            placeholder="Brief explanation of the correct answer"
            onChange={(e) => setQuestionDraft({ ...questionDraft, explanation: e.target.value })}
          />
        </form>
      </Modal>
    </div>
  );
}
